/**
 * MonthlyRollupPage — Weekly-to-Monthly Cash Rollup.
 * Monthly summary cards, budget comparison, grouped bar chart, and a
 * beginning→receipts→disbursements→ending waterfall per month.
 */

import React, { useMemo } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useForecastSummary, useMonthlyRollup } from './api';
import { formatCurrency, formatPct } from './utils';
import { cn } from '../../shared/lib/cn';
import type { MonthlyRollup } from './types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

function MonthSummaryCard({ month }: { month: MonthlyRollup }) {
  const netPositive = month.net_cash_flow >= 0;

  return (
    <Card>
      <CardContent className="p-5">
        <h4 className="text-sm font-semibold text-charcoal dark:text-gray-200 mb-3">
          {month.month_year}
        </h4>
        <div className="space-y-2 text-sm">
          <Row label="Beginning Cash" value={month.beginning_cash} />
          <Row label="Total Receipts" value={month.total_receipts} accent="text-emerald-600 dark:text-emerald-400" />
          <Row label="Total Disbursements" value={month.total_disbursements} accent="text-red-600 dark:text-red-400" />
          <div className="border-t border-gray-200 dark:border-dark-border pt-2">
            <Row
              label="Net Cash Flow"
              value={month.net_cash_flow}
              accent={netPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}
              bold
            />
          </div>
          <Row label="Ending Cash" value={month.ending_cash} bold />
          {month.total_availability != null && (
            <Row label="Total Availability" value={month.total_availability} />
          )}
          {month.covenant_headroom != null && (
            <Row
              label="Covenant Headroom"
              value={month.covenant_headroom}
              accent={
                month.covenant_headroom >= 0
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400'
              }
            />
          )}
        </div>
        {month.weeks_included && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-3">
            Weeks: {month.weeks_included}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  accent,
  bold,
}: {
  label: string;
  value: number;
  accent?: string;
  bold?: boolean;
}) {
  return (
    <div className="flex justify-between items-center">
      <span className={cn('text-gray-500 dark:text-gray-400', bold && 'font-semibold text-charcoal dark:text-gray-200')}>
        {label}
      </span>
      <span className={cn('font-mono tabular-nums', accent ?? 'text-charcoal dark:text-gray-200', bold && 'font-semibold')}>
        {formatCurrency(value, true)}
      </span>
    </div>
  );
}

function BudgetComparisonCard({ month }: { month: MonthlyRollup }) {
  const hasAnyBudget =
    month.budget_receipts != null || month.budget_disbursements != null;

  if (!hasAnyBudget) return null;

  const receiptVar = month.budget_variance_receipts ?? 0;
  const disbVar = month.budget_variance_disbursements ?? 0;

  return (
    <Card>
      <CardContent className="p-5">
        <h4 className="text-sm font-semibold text-charcoal dark:text-gray-200 mb-3">
          {month.month_year} — Budget vs Forecast
        </h4>
        <div className="space-y-3 text-sm">
          {month.budget_receipts != null && (
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                Receipts
              </p>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Budget</span>
                <span className="font-mono text-charcoal dark:text-gray-200">
                  {formatCurrency(month.budget_receipts, true)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Forecast</span>
                <span className="font-mono text-charcoal dark:text-gray-200">
                  {formatCurrency(month.total_receipts, true)}
                </span>
              </div>
              <div className="flex justify-between border-t border-gray-100 dark:border-dark-border pt-1 mt-1">
                <span className="font-medium text-charcoal dark:text-gray-200">Variance</span>
                <span
                  className={cn(
                    'font-mono font-semibold',
                    receiptVar >= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400',
                  )}
                >
                  {receiptVar >= 0 ? '+' : ''}
                  {formatCurrency(receiptVar, true)}
                </span>
              </div>
            </div>
          )}
          {month.budget_disbursements != null && (
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                Disbursements
              </p>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Budget</span>
                <span className="font-mono text-charcoal dark:text-gray-200">
                  {formatCurrency(month.budget_disbursements, true)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Forecast</span>
                <span className="font-mono text-charcoal dark:text-gray-200">
                  {formatCurrency(month.total_disbursements, true)}
                </span>
              </div>
              <div className="flex justify-between border-t border-gray-100 dark:border-dark-border pt-1 mt-1">
                <span className="font-medium text-charcoal dark:text-gray-200">Variance</span>
                <span
                  className={cn(
                    'font-mono font-semibold',
                    disbVar <= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400',
                  )}
                >
                  {disbVar > 0 ? '+' : ''}
                  {formatCurrency(disbVar, true)}
                </span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function WaterfallRow({ month }: { month: MonthlyRollup }) {
  const max = Math.max(
    Math.abs(month.beginning_cash),
    Math.abs(month.total_receipts),
    Math.abs(month.total_disbursements),
    Math.abs(month.ending_cash),
    1,
  );

  const barWidth = (v: number) => `${Math.min((Math.abs(v) / max) * 100, 100)}%`;

  const steps = [
    { label: 'Beginning', value: month.beginning_cash, color: 'bg-blue-400' },
    { label: '+ Receipts', value: month.total_receipts, color: 'bg-emerald-500' },
    { label: '− Disbursements', value: month.total_disbursements, color: 'bg-red-400' },
    { label: 'Ending', value: month.ending_cash, color: 'bg-indigo-500' },
  ];

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-charcoal dark:text-gray-200">{month.month_year}</p>
      {steps.map((s) => (
        <div key={s.label} className="flex items-center gap-3">
          <span className="text-xs text-gray-500 dark:text-gray-400 w-28 text-right shrink-0">
            {s.label}
          </span>
          <div className="flex-1 h-4 bg-gray-100 dark:bg-dark-surface-2 rounded overflow-hidden">
            <div
              className={cn('h-full rounded', s.color)}
              style={{ width: barWidth(s.value) }}
            />
          </div>
          <span className="text-xs font-mono w-20 text-right text-charcoal dark:text-gray-200 shrink-0">
            {formatCurrency(s.value, true)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function MonthlyRollupPage() {
  const { data: summary, isLoading: summaryLoading } = useForecastSummary();
  const forecastId = summary?.forecast_id ?? '';
  const { data: months, isLoading: rollupLoading } = useMonthlyRollup(forecastId);

  const isLoading = summaryLoading || rollupLoading;

  const chartData = useMemo(() => {
    if (!months) return [];
    return months.map((m) => ({
      month: m.month_year,
      forecast_receipts: m.total_receipts,
      budget_receipts: m.budget_receipts ?? 0,
      forecast_disbursements: m.total_disbursements,
      budget_disbursements: m.budget_disbursements ?? 0,
    }));
  }, [months]);

  const hasBudgetData = useMemo(
    () => months?.some((m) => m.budget_receipts != null || m.budget_disbursements != null) ?? false,
    [months],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-gray-400">Loading monthly rollup...</div>
      </div>
    );
  }

  if (!months || months.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100 mb-2">
            Monthly Rollup
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            No monthly rollup data available. Generate a forecast first.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 mt-2">
      {/* Monthly Summary Cards */}
      <div className={cn('grid gap-4', months.length <= 3 ? `grid-cols-1 md:grid-cols-${months.length}` : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4')}>
        {months.map((m) => (
          <MonthSummaryCard key={m.id} month={m} />
        ))}
      </div>

      {/* Budget Comparison */}
      {hasBudgetData && (
        <>
          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-200">
            Budget vs Forecast Comparison
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {months.map((m) => (
              <BudgetComparisonCard key={m.id} month={m} />
            ))}
          </div>
        </>
      )}

      {/* Grouped Bar Chart */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-200 mb-4">
            {hasBudgetData ? 'Forecast vs Budget by Month' : 'Receipts & Disbursements by Month'}
          </h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} barGap={2} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis
                tickFormatter={(v: number) => `$${(v / 1e6).toFixed(0)}M`}
                tick={{ fontSize: 12 }}
              />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey="forecast_receipts"
                name="Forecast Receipts"
                fill="#059669"
                radius={[2, 2, 0, 0]}
              />
              {hasBudgetData && (
                <Bar
                  dataKey="budget_receipts"
                  name="Budget Receipts"
                  fill="#6ee7b7"
                  radius={[2, 2, 0, 0]}
                />
              )}
              <Bar
                dataKey="forecast_disbursements"
                name="Forecast Disbursements"
                fill="#dc2626"
                radius={[2, 2, 0, 0]}
              />
              {hasBudgetData && (
                <Bar
                  dataKey="budget_disbursements"
                  name="Budget Disbursements"
                  fill="#fca5a5"
                  radius={[2, 2, 0, 0]}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Cash Flow Waterfall per Month */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-200 mb-4">
            Monthly Cash Flow Waterfall
          </h3>
          <div className="space-y-6">
            {months.map((m) => (
              <WaterfallRow key={m.id} month={m} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
